import React, { useState, useCallback, useRef } from 'react';
import * as MuiIcons from '@mui/icons-material';
import { getWidget } from './index';
import { getNestedValue, safeDisplayValue } from './utils';
import { UpdatePopover } from './MetricGridWidget';

/**
 * Section wrapper component
 * Supports detailAction in header: history, expand, update
 */
const Section = ({ icon: Icon, title, detailAction, onDetailAction, isExpanded, updateBadge, children }) => (
  <div className="mb-8 last:mb-0">
    <div className="flex items-center gap-2.5 mb-2 px-1">
      <div className="flex p-2 rounded-xl bg-indigo-500/10 text-indigo-400 shadow-inner">
        <Icon sx={{ fontSize: 20 }} />
      </div>
      <h3 className="text-[17px] font-black text-slate-100 tracking-tight m-0 uppercase opacity-90 flex-1">{title}</h3>
      {detailAction && detailAction.type !== 'update' && (
        <button
          onClick={onDetailAction}
          className="text-[10px] font-black transition-all uppercase tracking-tight text-indigo-400 hover:text-indigo-300 flex items-center gap-1 px-2 py-1 rounded-lg hover:bg-indigo-500/10"
        >
          {detailAction.type === 'expand' && isExpanded ? '접기' : '상세보기'}
          {detailAction.type === 'expand' && (
            isExpanded
              ? <MuiIcons.KeyboardArrowUp sx={{ fontSize: 14 }} />
              : <MuiIcons.KeyboardArrowDown sx={{ fontSize: 14 }} />
          )}
        </button>
      )}
      {detailAction?.type === 'update' && updateBadge}
    </div>
    <div
      className="bg-slate-800/20 rounded-2xl px-4 py-4 backdrop-blur-md"
      style={{ border: '1px solid rgba(255,255,255,0.06)', boxShadow: 'inset 0 0 20px rgba(0,0,0,0.2)' }}
    >
      {children}
    </div>
  </div>
);

/**
 * Dynamic Section Renderer
 * Renders a section with its widgets based on configuration.
 * 
 * Supports detailAction at section-level and widget-level:
 *   - type: "history"  → triggers onOpenHistory with popupType
 *   - type: "expand"   → toggles visibility of hidden widgets by targetIds
 * 
 * @param {Object} section - Section configuration from config_json
 * @param {Object} section.detailAction - { type: "history"|"expand", popupType?, targetIds? }
 * @param {Array}  section.widgets - Widget configs, may include { hidden: true, id: "..." }
 * @param {Object} data - Enriched machine data
 * @param {Function} onOpenHistory - Callback for opening history popup
 * @param {Function} onAction - Generic action callback for widgets
 */
export default function SectionRenderer({ 
  section, 
  data, 
  sendMessage,
  onOpenHistory,
  onAction
}) {
  // Dynamically get MUI icon component
  const IconComponent = MuiIcons[section.icon] || MuiIcons.Settings;

  // Track expanded widget IDs for "expand" mode
  const [expandedIds, setExpandedIds] = useState(new Set());

  // Update action state
  const [updateOpen, setUpdateOpen] = useState(false);
  const [updateLoading, setUpdateLoading] = useState(false);
  const badgeRef = useRef(null);

  const setUnityKeyboardCapture = useCallback((captureEnabled) => {
    if (!sendMessage) return;
    sendMessage("Canvas", "SetCaptureAllKeyboardInput", captureEnabled ? "true" : "false");
  }, [sendMessage]);

  // Helper to normalize targetIds to array
  const getSafeTargets = (ids) => {
    if (Array.isArray(ids)) return ids;
    if (typeof ids === 'string') return ids.split(',').map(s => s.trim()).filter(Boolean);
    return [];
  };

  // Handle detail action (section-level or widget-level)
  const handleDetailAction = useCallback((action) => {
    if (!action) return;

    if (action.type === 'history' && action.popupType) {
      // Open history popup (existing pattern)
      if (onOpenHistory) onOpenHistory(action.popupType);
    } else if (action.type === 'expand') {
      const targets = getSafeTargets(action.targetIds);
      if (targets.length === 0) return;

      // Toggle hidden widgets
      setExpandedIds(prev => {
        const next = new Set(prev);
        const allExpanded = targets.every(id => next.has(id));
        targets.forEach(id => {
          allExpanded ? next.delete(id) : next.add(id);
        });
        return next;
      });
    }
  }, [onOpenHistory]);

  // Handle update action confirm
  const handleUpdateConfirm = useCallback(async (newValue) => {
    if (!onAction) return;
    const action = section.detailAction;
    setUpdateLoading(true);
    try {
      await onAction('updateValue', {
        source_key: action.source_key,
        dataKey: action.dataKey,
        value: newValue,
      });
    } finally {
      setUpdateLoading(false);
      setUnityKeyboardCapture(true);
      setUpdateOpen(false);
    }
  }, [onAction, section.detailAction, setUnityKeyboardCapture]);

  // Resolve current value for update badge
  const updateAction = section.detailAction?.type === 'update' ? section.detailAction : null;
  let updateCurrentValue = null;
  if (updateAction) {
    const sk = updateAction.source_key;
    const dk = updateAction.dataKey;
    if (sk) {
      const sourceValue = getNestedValue(data, sk, undefined);
      const normalizedDataKey = dk && typeof dk === 'string' && dk.startsWith(`${sk}.`)
        ? dk.slice(`${sk}.`.length)
        : dk;

      if (normalizedDataKey && sourceValue && typeof sourceValue === 'object' && !Array.isArray(sourceValue)) {
        const nestedFromSource = getNestedValue(sourceValue, normalizedDataKey, undefined);
        if (nestedFromSource !== undefined) {
          updateCurrentValue = nestedFromSource;
        }
      }

      if (updateCurrentValue === null && normalizedDataKey) {
        const mergedPath = dk && dk.startsWith(`${sk}.`) ? dk : `${sk}.${normalizedDataKey}`;
        const nestedFromData = getNestedValue(data, mergedPath, undefined);
        if (nestedFromData !== undefined) {
          updateCurrentValue = nestedFromData;
        }
      }

      if (updateCurrentValue === null && sourceValue !== undefined) {
        updateCurrentValue = sourceValue;
      }
    } else if (dk) {
      updateCurrentValue = getNestedValue(data, dk, undefined);
    }
  }

  // Build update badge JSX
  const updateBadgeJsx = updateAction ? (
    <div className="relative flex items-center gap-2" ref={badgeRef}>
      <span className="text-[11px] text-slate-400 font-bold">{updateAction.label}</span>
      <button
        onClick={() => {
          const nextOpen = !updateOpen;
          setUnityKeyboardCapture(!nextOpen);
          setUpdateOpen(nextOpen);
        }}
        className="flex items-center gap-1 px-2.5 py-1 rounded-lg bg-indigo-500/15 hover:bg-indigo-500/25 transition-all group"
        style={{ border: '1px solid rgba(99,102,241,0.3)' }}
      >
        <span className="text-[12px] font-black text-indigo-300 group-hover:text-indigo-200">
          {safeDisplayValue(updateCurrentValue, '-')}
        </span>
        {updateAction.unit && (
          <span className="text-[10px] text-indigo-400/70 font-bold">{updateAction.unit}</span>
        )}
        <svg className="w-2.5 h-2.5 text-indigo-400/50 group-hover:text-indigo-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
        </svg>
      </button>
      <UpdatePopover
        isOpen={updateOpen}
        anchorRef={badgeRef}
        label={updateAction.label || '값'}
        currentValue={updateCurrentValue}
        unit={updateAction.unit}
        inputType={updateAction.inputType || 'text'}
        loading={updateLoading}
        onConfirm={handleUpdateConfirm}
        onClose={() => {
          setUnityKeyboardCapture(true);
          setUpdateOpen(false);
        }}
      />
    </div>
  ) : null;

  // Check if section-level expand is active
  const sectionTargets = section.detailAction?.type === 'expand' ? getSafeTargets(section.detailAction.targetIds) : [];
  const sectionExpanded = sectionTargets.length > 0 && sectionTargets.every(id => expandedIds.has(id));

  return (
    <Section
      icon={IconComponent}
      title={section.title}
      detailAction={section.detailAction}
      onDetailAction={() => handleDetailAction(section.detailAction)}
      isExpanded={sectionExpanded}
      updateBadge={updateBadgeJsx}
    >
      <div className="space-y-6">
        {section.widgets.map((widget, idx) => {
          // Hidden widget visibility check
          if (widget.hidden && !expandedIds.has(widget.id)) {
            return null;
          }

          const WidgetComponent = getWidget(widget.type);
          
          if (!WidgetComponent) {
            console.warn(`Widget type "${widget.type}" not found in registry`);
            return null;
          }

          // Support both DB-driven format (widget_options) and JSON config format (options)
          const rawOpts = widget.widget_options || widget.options || {};
          
          // Map options.source_key → options.dataKey for widget compatibility
          const widgetOpts = { ...rawOpts };
          if (widgetOpts.source_key && widgetOpts.dataKey && widgetOpts.source_key !== widgetOpts.dataKey) {
            // Both present and different -> assume dataKey is a nested path
            widgetOpts.dataKey = `${widgetOpts.source_key}.${widgetOpts.dataKey}`;
          } else if (widgetOpts.source_key && !widgetOpts.dataKey) {
            // Only source_key -> map to dataKey
            widgetOpts.dataKey = widgetOpts.source_key;
          } else if (!widgetOpts.source_key && widgetOpts.dataKey) {
            // Legacy/Direct mode - use dataKey as is
          }
          
          // Map source_key → dataKey for widget compatibility
          // If dataKey and source_key are distinct, we assume dataKey is a field within the source object
          const mappedItems = widget.items?.map(item => ({
            ...item,
            dataKey: (item.source_key && item.dataKey && item.source_key !== item.dataKey)
              ? `${item.source_key}.${item.dataKey}`
              : (item.dataKey || item.source_key)
          }));

          // Check for widget-level detailAction
          const widgetDetailAction = widget.detailAction || widgetOpts?.detailAction;
          // Calculate expansion state for widget action
          const widgetTargets = widgetDetailAction?.type === 'expand' ? getSafeTargets(widgetDetailAction.targetIds) : [];
          const isWidgetExpanded = widgetTargets.length > 0 && widgetTargets.every(id => expandedIds.has(id));

          return (
            <div key={`${section.id}-widget-${idx}`}>
              {/* Widget-level detail action button (above widget) */}
              {widgetDetailAction && (
                <div className="flex justify-end mb-1.5 px-1">
                  <button
                    onClick={() => handleDetailAction(widgetDetailAction)}
                    className="text-[10px] font-black transition-all uppercase tracking-tight text-indigo-400 hover:text-indigo-300 flex items-center gap-1"
                  >
                    {widgetDetailAction.type === 'expand' && isWidgetExpanded ? '접기' : '상세보기'}
                    {widgetDetailAction.type === 'expand' && (
                      isWidgetExpanded
                        ? <MuiIcons.KeyboardArrowUp sx={{ fontSize: 14 }} />
                        : <MuiIcons.KeyboardArrowDown sx={{ fontSize: 14 }} />
                    )}
                  </button>
                </div>
              )}

              {/* Expand animation wrapper for hidden widgets */}
              {widget.hidden ? (
                <div
                  className="overflow-hidden transition-all duration-500 ease-[cubic-bezier(0.2,1,0.2,1)]"
                  style={{
                    maxHeight: expandedIds.has(widget.id) ? '1000px' : '0px',
                    opacity: expandedIds.has(widget.id) ? 1 : 0,
                  }}
                >
                  <WidgetComponent
                    config={{
                      ...widgetOpts,
                      items: mappedItems,
                    }}
                    data={data}
                    sendMessage={sendMessage}
                    onOpenHistory={onOpenHistory}
                    onAction={onAction}
                  />
                </div>
              ) : (
                <WidgetComponent
                  config={{
                    ...widgetOpts,
                    items: mappedItems,
                  }}
                  data={data}
                  sendMessage={sendMessage}
                  onOpenHistory={onOpenHistory}
                  onAction={onAction}
                />
              )}
            </div>
          );
        })}
      </div>
    </Section>
  );
}

export { Section };
